import Big from 'big.js';
import { Formik } from 'formik';
import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import { Button, Buttons, CancelButton, Checkbox, Field, Form, ModalCard } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { dateFormats, emptyStringToNull, ordinal } from '~/utils';

const ContainerControl = styled.span`
  display: flex;
  align-items: center;
`;

export default function AddMultipleDialog({ currency, project, onSaved, onCancel }) {
  const { workspace } = useWorkspace();
  const api = useApi();

  const [defaultInvoiceItem, setDefaultInvoiceItem] = useState();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.www
          .workspaces(workspace.id)
          .projects(project.id)
          .defaultInvoiceItem({
            projectTypeId: project.projectType ? project.projectType.id : undefined,
          });

        setDefaultInvoiceItem(data);
      } catch {
        // Do nothing
      }
    })();
  }, [api, workspace.id, project]);

  const initialValues = {
    start: null,
    end: null,
    dayOfMonth: null,
    invoiceItem: defaultInvoiceItem,
    description: '',
    quantity: '',
    rate: '',
    fee: '',
  };

  const [includeQuantity, setIncludeQuantity] = useState(false);

  const toast = useToast();
  const handleSubmit = async (values, formik) => {
    formik.setSubmitting(true);

    const { start, end, dayOfMonth, invoiceItem, description, quantity, rate, fee } = emptyStringToNull(values);

    const { data: dates } = await api.www
      .workspaces(workspace.id)
      .projects(project.id)
      .generateRecurringDates({ start, end, dayOfMonth });

    const instances = dates.map((date) => {
      return emptyStringToNull({
        date: moment(date).format(dateFormats.isoDate),
        invoiceItemId: invoiceItem?.id,
        description,
        quantity,
        rate,
        fee,
      });
    });

    try {
      await api.www.workspaces(workspace.id).projectSettings(project.id).otherItems().batch.create(instances);
      toast.success('Other Items added successfully.');
      await onSaved();
    } catch (error) {
      toast.error('Failed to add other items.');
    }

    formik.setSubmitting(false);
  };

  if (!defaultInvoiceItem) return;

  return (
    <ModalCard title="Add Multiple Other Items" onClose={onCancel}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={Yup.object().shape({
          start: Yup.date().label('Starting Month').nullable().required(),
          end: Yup.date()
            .label('Ending Month')
            .nullable()
            .min(Yup.ref('start'), 'Ending Month must be after Starting Month')
            .required(),
          dayOfMonth: Yup.mixed()
            .label('Day of Month')
            .oneOf([...Array(28).keys()].map((i) => i + 1).concat('last'))
            .required(),
          invoiceItem: Yup.mixed().label('Invoice Item').required(),
          description: Yup.string().label('Notes').max(255).required(),
          quantity: Yup.number()
            .label('Quantity')
            .positive()
            .max(99999999.99)
            .nullable()
            .when('$includeQuantity', (_, schema) => (includeQuantity ? schema.required() : schema)),
          rate: Yup.number()
            .label('Rate')
            .min(-99999999999)
            .max(99999999999)
            .nullable()
            .when('$includeQuantity', (_, schema) => (includeQuantity ? schema.required() : schema)),
          fee: Yup.number().label('Amount').min(-99999999999).max(99999999999).required(),
        })}>
        {({ submitForm, ...formik }) => {
          const handleIncludeQuantityChange = ({ target: { checked } }) => {
            formik.setValues({
              ...formik.values,
              quantity: '',
              rate: '',
              fee: '',
            });
            setIncludeQuantity(checked);
          };

          const handleCalculateFee = () => {
            const quantity = _.isNumber(formik.values.quantity)
              ? _.round(formik.values.quantity, 2)
              : formik.values.quantity;

            const rate = _.isNumber(formik.values.rate) ? _.round(formik.values.rate, 7) : formik.values.rate;

            const fee =
              _.isNumber(formik.values.quantity) && _.isNumber(formik.values.rate)
                ? Big(quantity).times(rate).round(2).toNumber()
                : '';

            formik.setValues({ ...formik.values, fee, quantity, rate });
          };

          return (
            <>
              <ModalCard.Body>
                <Form.Control>
                  <Checkbox
                    label="Include a quantity and rate for these items"
                    checked={includeQuantity}
                    onChange={handleIncludeQuantityChange}
                  />
                </Form.Control>

                <Form.Control>
                  <Field.DayPicker
                    name="start"
                    placeholder="Starting Month"
                    locale="en-US"
                    displayFormat={dateFormats.monthYear}
                    scope="month"
                  />
                  <Field.DayPicker
                    name="end"
                    placeholder="Ending Month"
                    locale="en-US"
                    displayFormat={dateFormats.monthYear}
                    scope="month"
                  />
                </Form.Control>

                <Form.Control>
                  <ContainerControl style={{ marginTop: '0' }}>
                    Scheduled on the
                    <div style={{ margin: '0 0.5rem' }}>
                      <Field.SingleSelect
                        name="dayOfMonth"
                        style={{
                          width: '7rem',
                          margin: '0 0.5rem',
                          textAlign: 'right',
                        }}
                        materialPlaceholder={false}>
                        {[...Array(28).keys()].map((day) => (
                          <option key={day} value={day + 1}>
                            {ordinal(day + 1)}
                          </option>
                        ))}
                        <option value="last">Last</option>
                      </Field.SingleSelect>
                    </div>
                    <span>day of each month</span>
                  </ContainerControl>
                </Form.Control>

                <Form.Control>
                  <Field.InvoiceItemSelect
                    name="invoiceItem"
                    placeholder="Invoice Item"
                    type="income"
                    position="top"
                    clearable={false}
                  />
                </Form.Control>

                <Form.Control>
                  <Field.Text name="description" placeholder="Notes" maxLength={255} />
                </Form.Control>

                {includeQuantity && (
                  <Form.Control>
                    <Field.Number name="quantity" placeholder="Quantity" precision={2} onBlur={handleCalculateFee} />
                    <Field.Currency
                      name="rate"
                      placeholder="Rate"
                      precision={7}
                      currency={currency}
                      onBlur={handleCalculateFee}
                    />
                  </Form.Control>
                )}

                <Form.Control>
                  <Field.Currency name="fee" placeholder="Amount" disabled={includeQuantity} currency={currency} />
                </Form.Control>
              </ModalCard.Body>
              <ModalCard.Footer>
                <Buttons align="right">
                  <CancelButton onClick={onCancel}>Cancel</CancelButton>
                  <Button isLoading={formik.isSubmitting} onClick={submitForm}>
                    Add
                  </Button>
                </Buttons>
              </ModalCard.Footer>
            </>
          );
        }}
      </Formik>
    </ModalCard>
  );
}
